/**
 * @file ProtectedRoute.tsx
 * @description Route guard component that restricts access to authenticated users only.
 * Wraps dashboard routes and redirects unauthenticated visitors to the login page.
 *
 * How it works:
 * - Uses useAuth hook to check authentication state
 * - Shows a loading screen while auth state is being determined
 * - Redirects to /login if no user is authenticated
 * - Renders child routes via Outlet if authenticated
 *
 * @changelog
 * - 2026-03-19: Added documentation header
 */
import { Navigate, Outlet } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';

export default function ProtectedRoute() {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen bg-navy-950 flex items-center justify-center">
        <div className="text-navy-400 font-mono text-sm tracking-wider uppercase">Loading...</div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  return <Outlet />;
}
